import { get, values } from 'lodash'
import * as moment from 'moment'

import {
  AU_ZIPCODE_PATTERNS,
  CA_ZIPCODE_PATTERNS,
  JP_ZIPCODE_PATTERNS,
  STATES,
  US_ZIPCODE_PATTERNS,
} from '../constants'
import { FormFields } from '../utilities/Form'
import t from './validatorStrings'

/* tslint:disable-next-line */
const EMAIL_TEST = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const ALL_NON_DIGITS = /\D/g

const CHECKR_DRIVER_LICENSE_TEST = /^[-*0-9\sa-zA-Z]*$/
const CHECKR_SAFE_CHARACTERS_TEST = /^[a-zA-Z0-9ªµºÀ-ÖØ-öø-ÿ-\\ --'.,]*$/

const PASSWORD_MIN_LENGTH = 8

/**
 * Validates a driver's license number based on regex
 * provided by Checkr.
 * @param value The field value
 * @param _ not used
 * @param fields Fields from the rest of the form
 * @returns String or null
 */
export const validateCheckrDriversLicenseByState = (
  value: string,
  _,
  fields: FormFields
): string | null => {
  const stateCode: string = fields.licenseState.value.toString()
  const stateName: string = STATES[stateCode]
  if (value === null || value.length === 0) {
    return t.licenseNumberNull()
  }

  let isValid = null

  switch (stateName) {
    case STATES.AL:
      isValid = /^\d{7,8}$/.test(value)
      break
    case STATES.AK:
      isValid = /^\d{7}$/.test(value)
      break
    case STATES.AZ:
      isValid = /^[A-Z]{1}\d{8}$/.test(value) || /^\d{9}$/.test(value)
      break
    case STATES.AR:
      isValid = /^\d{9}$/.test(value)
      break
    case STATES.CA:
      isValid = /^[A-Z]{1}\d{7}$/.test(value)
      break
    case STATES.CO:
      isValid = /^\d{9}$/.test(value)
      break
    case STATES.CT:
      isValid = /^\d{9}$/.test(value)
      break
    case STATES.DE:
      isValid = /^\d{2,7}$/.test(value)
      break
    case STATES.DC:
      isValid = /^\d{7,9}$/.test(value)
      break
    case STATES.FL:
      isValid = /^[A-Z]{1}\d{12}$/.test(value)
      break
    case STATES.GA:
      isValid = /^\d{9}$/.test(value)
      break
    case STATES.HI:
      isValid = /^H\d{8}$/.test(value) || /^\d{9}$/.test(value)
      break
    case STATES.ID:
      isValid = /^[A-Z]{2}\d{6}[A-Z]{1}/.test(value) || /^\d{9}$/.test(value)
      break
    case STATES.IL:
      isValid = /^[A-Z]{1}\d{11,12}$/.test(value)
      break
    case STATES.IN:
      isValid = /^\d{10}$/.test(value)
      break
    case STATES.IA:
      isValid = /(^\d{9}$)/.test(value) || /(^\d{3}[A-Z]{2}\d{4}$)/.test(value)
      break
    case STATES.KS:
      isValid = /^[K]{1}\d{8}$/.test(value)
      break
    case STATES.KY:
      isValid = /^[A-Z]{1}\d{8}$/.test(value)
      break
    case STATES.LA:
      isValid = /^[0]{0,1}\d{7,8}$/.test(value)
      break
    case STATES.ME:
      isValid = /^\d{7}$/.test(value)
      break
    case STATES.MD:
      isValid = /^[A-Z]{1}\d{12}$/.test(value)
      break
    case STATES.MA:
      isValid =
        /^SA\d{7}$/.test(value) ||
        /^S\d{8}$/.test(value) ||
        /^\d{9}$/.test(value)
      break
    case STATES.MI:
      isValid = /^[A-Z]{1}\d{12}$/.test(value)
      break
    case STATES.MN:
      isValid = /^[A-Z]{1}\d{12}$/.test(value)
      break
    case STATES.MS:
      isValid = /^\d{9}$/.test(value)
      break
    case STATES.MO:
      isValid =
        /(^\d{9}$)/.test(value) ||
        /(^[A-Z]{1}\d{5,9}$)/.test(value) ||
        /(^\d{3}[A-Z]{1}\d{6}$)/.test(value)
      break
    case STATES.MT:
      isValid =
        /(^\d{13}$)/.test(value) ||
        /(^(\d|[A-Z]){9}$)/.test(value) ||
        /(^[A-Z]{3}\d{10}$)/.test(value)
      break
    case STATES.NE:
      isValid = /^[A-Z]{1}\d{3,8}$/.test(value)
      break
    case STATES.NV:
      isValid = /^\d{10}$/.test(value)
      break
    case STATES.NH:
      isValid =
        /(^\d{2}[A-Z]{3}\d{5}$)/.test(value) || /(^NHL\d{8}$)/.test(value)
      break
    case STATES.NJ:
      isValid = /^[A-Z]{1}\d{14}$/.test(value)
      break
    case STATES.NM:
      isValid = /^\d{9}$/.test(value)
      break
    case STATES.NY:
      isValid = /(^\d{9}$)/.test(value) || /(^[A-Z]{1}\d{18}$)/.test(value)
      break
    case STATES.NC:
      isValid = /^\d{1,12}$/.test(value)
      break
    case STATES.ND:
      isValid = /^(\d|[A-Z]){9}$/.test(value)
      break
    case STATES.OH:
      isValid = /^[A-Z]{2}\d{6}$/.test(value) || /(^\d{8}$)/.test(value)
      break
    case STATES.OK:
      isValid = /(^[A-Z]{1}\d{9}$)/.test(value) || /(^\d{9}$)/.test(value)
      break
    case STATES.OR:
      isValid = /(^\d{5,7}$)/.test(value) || /(^[A-Z]{1}\d{6}$)/.test(value)
      break
    case STATES.PA:
      isValid = /^\d{8}$/.test(value)
      break
    case STATES.PR:
      isValid = /^\d{5,7}$/.test(value) || /^\d{9}$/.test(value)
      break
    case STATES.RI:
      isValid = /(^\d{7,8}$)/.test(value) || /(^V\d{6}$)/.test(value)
      break
    case STATES.SC:
      isValid = /^\d{3,10}$/.test(value)
      break
    case STATES.SD:
      isValid = /^\d{6,9}$/.test(value)
      break
    case STATES.TN:
      isValid = /^\d{7,9}$/.test(value)
      break
    case STATES.TX:
      isValid = /^\d{7,8}$/.test(value)
      break
    case STATES.UT:
      isValid = /^\d{4,10}$/.test(value)
      break
    case STATES.VT:
      isValid = /(^\d{8}$)/.test(value) || /(^\d{7}A$)/.test(value)
      break
    case STATES.VA:
      isValid = /^[A-Z]{1}\d{8}$/.test(value) || /^\d{9}$/.test(value)
      break
    case STATES.WA:
      isValid =
        /^[A-Z*]{4,8}\d{2,3}(\d|[A-Z]){2}$/.test(value) ||
        /^WDL([A-Z]|\d){9}$/.test(value)
      break
    case STATES.WV:
      isValid = /^([A-Z]|\d){7}$/.test(value)
      break
    case STATES.WI:
      isValid = /^[A-Z]{1}\d{13}$/.test(value)
      break
    case STATES.WY:
      isValid = /^\d{9}$/.test(value)
      break
    default:
      return t.licenseStateNull()
  }

  if (!isValid) {
    // TODO: Update this to a new string
    return t.licenseNumberNull()
  }

  return null
}

export function formatNumberOnly(str) {
  return (str || '').replace(ALL_NON_DIGITS, '')
}

export function isValidPhoneNumber(phoneNumber = '') {
  const phoneNumberJustDigits: string = formatNumberOnly(phoneNumber)
  const { length } = phoneNumberJustDigits
  return (
    length === 9 ||
    length === 10 ||
    (length === 11 && phoneNumber[0] === '1') ||
    (phoneNumber[0] === '+' && length >= 11)
  )
}

export function isValidEmail(email = '') {
  return EMAIL_TEST.test(email)
}

export function isValidZipCode(str = '') {
  return (
    US_ZIPCODE_PATTERNS.test(str) ||
    CA_ZIPCODE_PATTERNS.test(str.replace(/\W+/g, '')) ||
    AU_ZIPCODE_PATTERNS.test(str) ||
    JP_ZIPCODE_PATTERNS.test(str)
  )
}

export const validators = {
  email: value => {
    if (!value || value.length === 0) {
      return t.emailNull()
    }
    if (!isValidEmail(value)) {
      return t.emailInvalid()
    }
    return null
  },
  phoneNumber: value => {
    if (!value || value.length === 0) {
      return t.phoneNumberInvalid()
    }
    if (!isValidPhoneNumber(value)) {
      return t.phoneNumberInvalid()
    }
    return null
  },
  optionalPhoneNumber: value => {
    if (value && value.length > 0 && !isValidPhoneNumber(value)) {
      return t.optionalPhoneNumberInvalid()
    }
    return null
  },
  zipCode: value => {
    if (!value || value.length === 0) {
      return t.zipCodeNull()
    }
    if (!isValidZipCode(value)) {
      return t.zipCodeInvalid()
    }
    return null
  },
  legalTextCheckBox: value => {
    if (!value) {
      return t.legalTextUnchecked()
    }
    return null
  },
  firstName: value => {
    if (!value || value.length === 0) {
      return t.firstNameNull()
    }
    if (!value || value.length < 2) {
      return t.firstNameInvalidLength()
    }
    return null
  },
  shortFirstName: value => {
    if (!value || values.length === 0) {
      return t.firstNameNull()
    }
    return null
  },
  firstNameCheckr: value => {
    if (!value || value.length === 0) {
      return t.firstNameNull()
    }

    if (!value || value.length < 2) {
      return t.firstNameInvalidLength()
    }

    if (!CHECKR_SAFE_CHARACTERS_TEST.test(value)) {
      return t.firstNameInvalidCharacters()
    }

    return null
  },
  middleName: value => {
    if (!value || value.length === 0) {
      return t.middleNameNull()
    }
    return null
  },
  middleNameCheckr: value => {
    if (!value || value.length === 0) {
      return t.middleNameNull()
    }

    if (!CHECKR_SAFE_CHARACTERS_TEST.test(value)) {
      return t.middleNameInvalidCharacters()
    }

    return null
  },
  lastName: value => {
    if (!value || value.length === 0) {
      return t.lastNameNull()
    }
    if (!value || value.length < 2) {
      return t.lastNameInvalidLength()
    }
    return null
  },
  lastNameCheckr: value => {
    if (!value || value.length === 0) {
      return t.lastNameNull()
    }

    if (!value || value.length < 2) {
      return t.lastNameInvalidLength()
    }

    if (!CHECKR_SAFE_CHARACTERS_TEST.test(value)) {
      return t.lastNameInvalidCharacters()
    }

    return null
  },
  shortLastName: value => {
    if (!value || value.length === 0) {
      return t.lastNameNull()
    }
    return null
  },
  password: value => {
    if (!value) {
      return t.passwordNull()
    }
    if (value.length < PASSWORD_MIN_LENGTH) {
      return t.passwordInvalidLength()
    }
    return null
  },
  confirmPassword: (value: string, _, fields: FormFields) => {
    if (!value) {
      return t.passwordNull()
    }
    if (value.length < PASSWORD_MIN_LENGTH) {
      return t.passwordInvalidLength()
    }

    const inputPassword: string = fields.password.value.toString()
    if (value !== inputPassword) {
      return t.passwordConfirmNoMatch()
    }

    return null
  },
  licenseNumber: value => {
    if (
      !value ||
      value.length === 0 ||
      !CHECKR_DRIVER_LICENSE_TEST.test(value)
    ) {
      return t.licenseNumberNull()
    }
    return null
  },
  licenseState: value => {
    if (!value || value.length === 0) {
      return t.licenseStateNull()
    }
    return null
  },
  vehicleType: value => {
    if (!value || value.length === 0 || value <= 0) {
      return t.vehicleTypeNull()
    }
    return null
  },
  insuranceCheck: value => {
    if (!value) {
      return t.insuranceCheckNull()
    }
    return null
  },
  ssn: value => {
    if (!value || value.length < 9) {
      return t.ssnInvalid()
    }
    return null
  },
  confirmSsn: (value: string, _, fields: FormFields) => {
    if (!value || value.length < 9) {
      return t.ssnInvalid()
    }

    const inputSsn: string = fields.ssn.value.toString()
    if (value !== inputSsn) {
      return t.ssnConfirmNoMatch()
    }

    return null
  },
  radioButtonGroup: value => {
    if (value === undefined) {
      return t.backgroundCheckCitizenship()
    }
    return null
  },
  birthday: value => {
    // At least 8 chars because 1-1-2000 should be valid
    // as well as 1-01-2000 and 01-1-2000
    if (!value || value.length < 8) {
      return t.birthdayInvalid()
    }

    const minBirthday = moment()
      .subtract(18, 'years')
      .format('YYYY-MM-DD')
    const minDate = moment()
      .subtract(120, 'years')
      .format('YYYY-MM-DD')

    // https://github.com/moment/moment/issues/5232
    // Months are 0 indexed, 0-11
    const dob = value.split('-')
    const birthdate = moment({
      year: dob[0],
      month: dob[1] - 1,
      day: dob[2],
    }).format('YYYY-MM-DD')

    if (birthdate < minDate || birthdate > moment().format('YYYY-MM-DD')) {
      return t.birthdayInvalidDate()
    }

    if (birthdate > minBirthday) {
      return t.birthdayInvalidMinDate()
    }

    return null
  },
  requestedBgCheckCopy: () => {
    // Optional field
    return null
  },
  redCardLastFour: value => {
    if (!value) {
      return t.redCardLastFourNull()
    }

    if (value.length !== 4) {
      return t.redCardLastFourInvalidLength()
    }

    return null
  },
  redCardDelightNumber: value => {
    if (!value) {
      return t.redCardDelightNumberNull()
    }

    if (value.length < 5 || value.length > 10) {
      return t.redCardDelightNumberInvalidLength()
    }

    return null
  },
  hidden: () => {
    // For hidden fields
    return null
  },
  australianBusinessNumber: value => {
    if (!value || value.length < 11) {
      return t.abnInvalid()
    }
    return null
  },
  isGstRegistered: () => {
    // Optional field
    return null
  },

  // TODO:: This currently only supports US,
  // need to move stripe validation into forms

  isValidBankAccountNumber: value => {
    if (!value) {
      return 'Please enter a valid Account Number'
    }
    if (value.length !== 12) {
      return 'Account number must have 12 digits'
    }
    return null
  },
  isValidBankRoutingNumber: value => {
    if (!value) {
      return 'Please enter a valid Routing Number'
    }
    if (value.length !== 9) {
      return 'Routing number must have 9 digits'
    }
    return null
  },
  vehicle: {
    make: value => (!value || value.length === 0 ? t.makeError() : null),
    model: value => (!value || value.length === 0 ? t.modelError() : null),
    color: value => (!value || value.length === 0 ? t.colorError() : null),
  },
}
